import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { exportVisibleColumns } from "./tableExcel";
import { nextMobileSort, sortMobileRows, type MobileSortState } from "./mobileSorting";
import { storeOverviewComparisonLabel, storeOverviewCopy } from "./storeOverviewPresentation";
import { categoryOverviewLayout, type CategoryColumn, type CategoryOverviewVariant, type ExistingCategoryColumn } from "./categoryOverviewPresentation";
import { desktopMetricHelp } from "./desktopInformation";
import TableFullText from "./TableFullText";
import { TableWindowSpacer, useTableWindow } from "./tableWindow";
import "./storeOverview.css";
import "./categoryOverview.css";

type DisplayRow = Record<string, string | number | null>;
type Props = {
  rows: DisplayRow[];
  totalRow: DisplayRow;
  columns: ExistingCategoryColumn[];
  language: string;
  variant?: CategoryOverviewVariant;
  singleLabel?: string;
  hiddenColumns: Set<string>;
  ariaLabel: string;
  currentSort: () => MobileSortState;
  parseNumber: (value: unknown) => number | null;
  onViewChange: (sort: MobileSortState, rowIds: string[]) => void;
  children: ReactNode;
};
const mobileQuery = "(max-width: 900px), (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 600px)";

export default function CategoryOverviewTable(props: Props) {
  const [mobile, setMobile] = useState(() => window.matchMedia(mobileQuery).matches);
  useEffect(() => {
    const media = window.matchMedia(mobileQuery);
    const update = () => setMobile(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return mobile ? props.children : <DesktopCategoryOverview {...props} />;
}

function DesktopCategoryOverview(props: Props) {
  const columns = categoryOverviewLayout(props.columns, props.language, props.hiddenColumns, props.variant);
  const [sort, setSort] = useState(props.currentSort);
  const c = storeOverviewCopy(props.language);
  const columnSignature = JSON.stringify(props.columns.filter(column => !props.hiddenColumns.has(column.key)).map(column => column.key));
  useEffect(() => {
    exportVisibleColumns.set(props.ariaLabel, JSON.parse(columnSignature));
    return () => { exportVisibleColumns.delete(props.ariaLabel); };
  }, [props.ariaLabel, columnSignature]);
  const sorted = useMemo(() => sortMobileRows(props.rows, sort, (row, key) => key === "label" || key === "store"
    ? String(row["__sort_" + key] ?? row[key] ?? "")
    : props.parseNumber(row["__sort_" + key] ?? row[key])), [props.rows, sort, props.parseNumber]);
  const rowSignature = useMemo(() => JSON.stringify(sorted.map(row => row.__row_id ?? row.label)), [sorted]);
  const windowed = useTableWindow(sorted.length, rowSignature + JSON.stringify(sort));
  useEffect(() => { props.onViewChange(sort, sorted.map(row => String(row.__row_id ?? row.label))); }, [rowSignature, sort, props.onViewChange]);
  const tone = (row: DisplayRow, key: string) => {
    if (!key.endsWith("Change")) return "";
    const value = String(row[key] ?? "");
    return value.startsWith("+") ? "is-positive" : value.startsWith("-") ? "is-negative" : "";
  };
  const sortButton = (column: CategoryColumn, comparison = false) => <button type="button" data-sort-column={column.key}
    aria-label={column.label + " · " + c[25]} onClick={() => setSort(nextMobileSort(sort, column.key))} title={desktopMetricHelp(column.key, props.language)}>
    {sort?.key === column.key ? sort.direction === "asc" ? <ArrowUp size={12} aria-hidden /> : <ArrowDown size={12} aria-hidden /> : null}
    <span>{comparison ? storeOverviewComparisonLabel(props.language) : column.label}</span>
  </button>;
  const renderRow = (row: DisplayRow, total: boolean, rowIndex?: number) => <tr aria-rowindex={rowIndex} key={total ? "__total" : String(row.__row_id ?? row.label)} className={total ? "store-overview-total" : undefined}>
    {columns.map(column => <td key={column.key} data-column={column.key} title={column.key === "label" || column.key === "store" ? String(row[column.key] ?? "—") : undefined} className={[column.key === "label" || column.key === "store" ? "store-overview-label" : "", tone(row, column.key)].filter(Boolean).join(" ")}>
      {column.comparison ? <span className="store-overview-ly-pair">
        <span data-metric={column.key}>{String(row[column.key] ?? "—")}</span>
        <span data-metric={column.comparison.key} className={tone(row, column.comparison.key)} title={column.comparison.label}>{String(row[column.comparison.key] ?? "—")}</span>
      </span> : column.key === "label" && total && props.singleLabel ? `${String(row.label)} · ${props.singleLabel}` : column.key === "label" && !total && String(row.label ?? "").length > 30 ? <TableFullText text={String(row.label)} label={column.label} language={props.language} /> : String(row[column.key] ?? "—")}
    </td>)}
  </tr>;
  const width = columns.reduce((sum, column) => sum + column.width, 0);
  return <div className={`store-overview-view is-fit category-overview-table${props.columns.some(column => column.key === "store") ? " has-store" : ""}`}>
    <div className="store-overview-scroll" role="region" aria-label={props.ariaLabel} tabIndex={0} ref={windowed.scrollRef} onScroll={windowed.onScroll}>
      <table className="store-overview-table" style={{ minWidth: width }} aria-rowcount={props.singleLabel ? 2 : sorted.length + 2}>
        <caption className="store-overview-sr-only">{props.ariaLabel}</caption>
        <colgroup>{columns.map(column => <col key={column.key} style={{ width: column.width / width * 100 + "%" }} />)}</colgroup>
        <thead><tr className="store-overview-headings">{columns.map(column => <th key={column.key} scope="col" data-column={column.key} data-comparison-column={column.comparison?.key}
          className={[column.key === "label" || column.key === "store" ? "store-overview-label" : "", column.comparison ? "store-overview-paired-heading" : ""].filter(Boolean).join(" ")}
          aria-sort={sort && (sort.key === column.key || sort.key === column.comparison?.key) ? sort.direction === "asc" ? "ascending" : "descending" : "none"}>
          {sortButton(column)}{column.comparison ? sortButton(column.comparison, true) : null}
        </th>)}</tr></thead>
        <tbody>{renderRow(props.totalRow, true, 2)}{!props.singleLabel ? <><TableWindowSpacer height={windowed.before} columns={columns.length} />
          {sorted.slice(windowed.start, windowed.end).map((row, index) => renderRow(row, false, windowed.start + index + 3))}
          <TableWindowSpacer height={windowed.after} columns={columns.length} /></> : null}</tbody>
      </table>
    </div>
  </div>;
}
